import React, { useCallback, useMemo, useState } from 'react';
import CloseButton from '../CloseButton/CloseButton';
import styles from './AnalyticsInfo.module.scss';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon/Icon';
import cx from 'classnames';
import { parseString } from '../../utils/stringHelper';
import MiniUserDetail from '../MiniUserDetail/MiniUserDetail';

const pageTypes = {
  mainInfo: 'mainInfo',
  userInfo: 'userInfo',
  callToActionInfo: 'callToActionInfo',
};

const AnalyticsInfo = ({ info = [], onClose, isMobileView, inputRef, className }) => {
  const [pageType, setPageType] = useState(pageTypes.mainInfo);
  const [selectedUser, setSelectedUser] = useState({});
  const [selectedCallToAction, setSelectedCallToAction] = useState({});

  const allViews = useMemo(() => info?.views?.reduce((acc, { views }) => acc + views, 0), [info]);
  const currentCallToActionClicks = useCallback(users => users?.reduce((acc, { clicks }) => acc + clicks, 0) || 0, []);

  const { t } = useTranslation();

  const handleOpenUserInfo = user => {
    setSelectedUser(user);
    setPageType(pageTypes.userInfo);
  };

  const handleOpenCallToActionInfo = cta => {
    setSelectedCallToAction(cta);
    setPageType(pageTypes.callToActionInfo);
  };

  const handleCloseDetailsInfo = () => {
    setSelectedUser({});
    setSelectedCallToAction({});
    setPageType(pageTypes.mainInfo);
  };

  return (
    <div ref={inputRef} className={cx(styles.mainContainer, className, { [styles.mobileContainer]: isMobileView })}>
      <div className={cx(styles.mainWrapper, { [styles.hide]: pageType !== pageTypes.mainInfo })}>
        <div className={styles.closeContainer}>
          <CloseButton onClose={onClose} />
        </div>
        {/* Users general */}
        <span className={styles.titleSection}>{t('analytics.engagement_insights')}</span>
        <div className={styles.totalViewsContainer}>
          <span className={styles.totalViewsText}>{t('views.total_views')}</span>
          <span className={styles.totalViewsAmount}>{allViews}</span>
        </div>
        <div className={styles.uniqueViewersContainer}>
          <span className={styles.uniqueViewers}>
            {info?.views?.length} {t('views.unique_viewers')}
          </span>
          <span className={styles.clickTooltip}>{t('views.click_tooltip')}</span>
        </div>
        <div className={styles.viewerList}>
          {info?.views?.map(({ user, userId, views }) => (
            <div
              key={userId}
              className={cx(styles.viewerItem, styles.viewerItemClickable)}
              onClick={() => handleOpenUserInfo({ user, userId, views })}
            >
              {user ? (
                <MiniUserDetail
                  classNames={{
                    containerClassName: styles.profile,
                  }}
                  avatar={user.avatar}
                  size="medium"
                  name={parseString(user.name)}
                />
              ) : (
                  <div>
                  <div className={styles.imageContainer}>
                    <span>{views}</span>
                  </div>
                  <span className={styles.viewerName}>{parseString(user?.name) || t('views.anonymous')}</span>
                  </div>
              )}
            </div>
          ))}
        </div>
        {/* Call-to-actions general */}
        <div className={styles.ctaWrapper}>
          <span className={styles.titleSection}>{t('analytics.cta_insights')}</span>
          <div className={styles.ctaText}>
            <span>{t('analytics.cta')}</span>
            <span>{t('analytics.individual_clicks')}</span>
          </div>
          <div className={styles.actionsWrapper}>
            {info?.cta?.map(action => (
              <div key={action.id} className={styles.actionWrapper} onClick={() => handleOpenCallToActionInfo(action)}>
                <div className={styles.ctaInfo}>
                  <span className={styles.ctaName}>{action.text}</span>
                  <span className={styles.ctaLink}>{action.url}</span>
                </div>
                <span className={styles.totalViewsAmount}>{currentCallToActionClicks(action.users)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/*User details*/}
      <div className={cx(styles.userDetails, { [styles.hide]: !(pageType === pageTypes.userInfo) })}>
        <div className={styles.closeContainerDetails}>
          <div className={styles.backContainer} onClick={handleCloseDetailsInfo}>
            <Icon src="viewsBackIcon" className={styles.backIcon} />
            <span>{t('views.back')}</span>
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className={styles.viewerItem}>
          <div className={styles.imageContainer}>
            {selectedUser.user ? (
              <div>
                {selectedUser.user?.avatar ? <img className={styles.image} src={selectedUser.user?.avatar} alt={t('clip_organizer.add_modal.avatar')} />
                  : <span>{parseString(selectedUser.user?.name)[0]?.toUpperCase()}</span>}
              </div>
            ) : (
              <Icon className={styles.placeholder} src="viewerPlaceholder" />
            )}
          </div>
          <span className={styles.viewerName}>{selectedUser.user?.name ? parseString(selectedUser.user?.name) : t('views.anonymous')}</span>
        </div>
        <div className={styles.totalViewsContainer}>
          <span className={styles.totalViewsText}>{t('views.total_views')}</span>
          <span className={styles.totalViewsAmount}>{selectedUser.views || 0}</span>
        </div>
      </div>
      {/*Call-to-action details*/}
      <div className={cx(styles.containerDetails, { [styles.hide]: !(pageType === pageTypes.callToActionInfo) })}>
        <div className={styles.closeContainerDetails}>
          <div className={styles.backContainer} onClick={handleCloseDetailsInfo}>
            <Icon src="viewsBackIcon" className={styles.backIcon} />
            <span>{t('views.back')}</span>
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className={styles.actionDetails}>
          {selectedCallToAction?.users?.map(({ user, clicks }) => (
            <div key={user?.username} className={styles.ctaUser}>
              <div className={styles.ctaUserItem}>
                {user?.name ? (
                  <MiniUserDetail
                    avatar={user.avatar}
                    size="medium"
                    name={parseString(user.name)}
                    classNames={{
                      containerClassName: styles.profile,
                      avatarClassName: styles.ctaUserAvatar,
                      titleClassName: styles.ctaUserName,
                    }}
                  />
                ) : (
                    <div>
                    <div className={styles.imageContainer}>
                      <span>{clicks}</span>
                    </div>
                    <span className={styles.viewerName}>{t('views.anonymous')}</span>
                    </div>
                )}
              </div>
              <span className={styles.ctaItemCount}>{clicks}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsInfo;
